/**
 * The club link's landing (deep-links D12): `/?tenant-handle&club-id&get-app=1`
 * is where a club link for the generic app arrives when the app is not installed.
 * The download sheet opens on load, the Play button carries the club as an install
 * referrer, and the sheet offers to open an already-installed app at the same page.
 *
 * Design: docs/refactors/deep-links/PLAN.md.
 *
 * @module features/appDownload/clubAppLink
 */
import { buildAppLink, clubInstallReferrer, isRealAndroidPackageName, isRealIosAppId } from '@butter/library/shared';
import type { TenantAppConfig } from '@butter/library/shared';
import { getDeviceType } from '@butter/ui/domains/app';
import { QueryParams } from '~/lib/queryParams';
import { appRuntime } from '~/services/appRuntime/appRuntimeService';

/**
 * Whether this load arrived through a club link, on a phone. Pure — safe as a
 * state initializer (StrictMode runs those twice). Phones only: the sheet is a
 * mobile bottom sheet with no desktop design, and a desktop visitor has no app to get.
 */
export function arrivedForTheApp(): boolean {
  return new URLSearchParams(window.location.search).get(QueryParams.GET_APP) === '1' && getDeviceType() !== 'desktop';
}

/**
 * What the download sheet adds for the current club: the Play install referrer and
 * a deep link to this club in the app. A tenant's own store ids reach the portal
 * only once that platform is live, so both present means its white-label app is
 * live; otherwise its members use the generic app.
 */
export function clubAppLinks(tenant?: TenantAppConfig): { referrer?: string; openAppUrl?: string } {
  const handle = appRuntime.tenantHandle;
  const clubId = Number(appRuntime.clubId);
  if (!handle || !Number.isInteger(clubId) || clubId <= 0) return {};

  const live = isRealIosAppId(tenant?.iosAppId) && isRealAndroidPackageName(tenant?.androidPackageName);
  return {
    referrer: clubInstallReferrer(handle, clubId),
    openAppUrl: buildAppLink({ handle, appStatus: live ? 'live' : 'none' }, clubId, '/'),
  };
}
